import { useMemo, useState } from "react";
import { BounceCanvas } from "./BounceCanvas";
import type {
	BounceProps,
	InternalBounce,
	CubicBounceLerp,
} from "../types/bounce-types";
import { BuiltIn } from "../utilities/bezier-plugin";
import { useInputContext, useInputSetters, Components } from "leva/plugin";
import { StyledButton } from "../styles/StyledButton";
import { StyledIcons } from "../styles/StyledIcons";
import { StyledReturnButton } from "../styles/StyledReturnButton";
import { useGlobalState } from "../hooks/useGlobalState";
import { BezierProps } from "../types/bezier-types";
import { applyTool } from "../utilities/layers";
import { StyledBounceControlsModal } from "../styles/StyledBounceControlsModal";
import { ToolApplyRequest } from "../types/general-types";
import { UITools } from "../types/context-types";
import { StyledSVGOffset } from "../styles/StyledSVGOffset";
import {
	defaultBounceElasticity,
	defaultBounceGravity,
	defaultBounceHang,
	defaultBounceVelocity,
	defaultDecayX,
	defaultDecayY,
} from "../assets/constants";
import TopSection from "./TopSection";
import HoverModalButton from "./HoverModalButton";
import InputSlider from "./InputSlider";
import { HandlesModalButton } from "./HandlesModalButton";
import { DividerX } from "../styles/DividerX";
import { useNotifications } from "../context/NotificationProvider";
import HorizontalScroll from "./HorizontalScroll";

const { Row, Vector, Select } = Components;
const optionKeys = ["custom", ...Object.keys(BuiltIn)];
const optionValues = [
	false,
	...Object.values(BuiltIn).map((c) => c.toString()),
];

export function Bounce() {
	const { value, label, displayValue, onUpdate, onChange, settings } =
		useInputContext<BounceProps>();
	const { dispatch, UIStatus, configSettings } = useGlobalState();
	const [sliderControlsModal, setSliderControlsModal] = useState(true);
	const [elasticitySliderValue, setElasticitySliderValue] = useState(
		defaultBounceElasticity
	);
	const [gravitySliderValue, setGravitySliderValue] =
		useState(defaultBounceGravity);
	const [decayYSliderValue, setDecayYValue] = useState(defaultDecayY);
	const [decayXSliderValue, setDecayXValue] = useState(defaultDecayX);
	const [hangSliderValue, setHangSliderValue] = useState(defaultBounceHang);
	const [velocitySliderValue, setVelocitySliderValue] = useState(
		defaultBounceVelocity
	);
	const [bounceValues, setBounceValues] = useState<CubicBounceLerp[]>([]);
	const { addNotification } = useNotifications();

	async function applyBounceHandler(value: InternalBounce) {
		let req: ToolApplyRequest = {
			tool: UITools.bounce,
			// @ts-ignore
			values: bounceValues,
			userVars: {},
			config: configSettings,
		};
		const json = await applyTool(req);
		if (json) {
			console.log(json);
			const res = JSON.parse(json);
			if (!res.success) {
				addNotification({
					message: res.message,
					type: "error",
					title: "Error",
				});
				return;
			}
			addNotification({ message: "", type: "success", title: "Success" });
		}
	}

	function handleElasticitySliderChange(
		e: React.ChangeEvent<HTMLInputElement>
	) {
		setElasticitySliderValue(parseInt(e.target.value));
		onUpdate({ elasticity: e.target.value });
	}
	function handleGravitySliderChange(e: React.ChangeEvent<HTMLInputElement>) {
		setGravitySliderValue(parseInt(e.target.value));
		onUpdate({ gravity: e.target.value });
	}
	function handleDecayXChange(e: React.ChangeEvent<HTMLInputElement>) {
		setDecayXValue(parseInt(e.target.value));
		onUpdate({ decayX: e.target.value });
	}
	function handleDecayYChange(e: React.ChangeEvent<HTMLInputElement>) {
		setDecayYValue(parseInt(e.target.value));
		onUpdate({ decayY: e.target.value });
	}
	function handleHangSliderChange(e: React.ChangeEvent<HTMLInputElement>) {
		setHangSliderValue(parseInt(e.target.value));
		onUpdate({ hang: e.target.value });
	}
	function handleVelocitySliderChange(e: React.ChangeEvent<HTMLInputElement>) {
		setVelocitySliderValue(parseInt(e.target.value));
		onUpdate({ velocity: e.target.value });
	}

	function requestToolSwitch(tool: string): void {
		if (UIStatus.currentDisplay == "bounce" && tool == "bounce") {
			dispatch({
				type: "UIStatusUpdate",
				payload: {
					id: Date.now(),
					currentDisplay: "bezier",
					initializationTime: Date.now(),
				},
			});
		} else {
			dispatch({
				type: "UIStatusUpdate",
				payload: {
					id: Date.now(),
					currentDisplay: "elastic",
					initializationTime: Date.now(),
				},
			});
		}
	}
	return (
		<>
			<TopSection />
			<BounceCanvas setBounceValues={setBounceValues} />
			<div className="flex flex-col w-full items-center gap-0">
				<div className="relative flex items-center w-full h-10 px-1.5 py-1">
					<HorizontalScroll>
						<div className="flex h-full">
							<HandlesModalButton
								type={"Out"}
								displayValue={{ x1: 0.5, x2: 0.5, y1: 0.5, y2: 0.5 }}
								onUpdate={onUpdate}
								disabled={true}
							/>
							<HandlesModalButton
								type={"In"}
								displayValue={{ x1: 0.5, x2: 0.5, y1: 0.5, y2: 0.5 }}
								onUpdate={onUpdate}
								disabled={true}
							/>
						</div>
						<DividerX />
						<div className="flex h-full">
							<HoverModalButton
								onClick={() => requestToolSwitch("bounce")}
								label={"Bounce"}
								modalAlign="center_left"
								staticOn={true}
							>
								<InputSlider
									label={"Bounciness"}
									min={1}
									max={100}
									step={1}
									value={elasticitySliderValue}
									onChange={handleElasticitySliderChange}
								/>
								<InputSlider
									label={"Amplitude"}
									min={1}
									max={100}
									step={1}
									value={velocitySliderValue}
									onChange={handleVelocitySliderChange}
								/>
								<InputSlider
									label={"Gravity"}
									min={1}
									max={100}
									step={1}
									value={gravitySliderValue}
									onChange={handleGravitySliderChange}
								/>
								<InputSlider
									label={"Decay Y"}
									min={1}
									max={100}
									step={1}
									value={decayYSliderValue}
									onChange={handleDecayYChange}
								/>
								<InputSlider
									label={"Decay X"}
									min={1}
									max={100}
									step={1}
									value={decayXSliderValue}
									onChange={handleDecayXChange}
								/>
								<InputSlider
									label={"Hang"}
									min={1}
									max={100}
									step={1}
									value={hangSliderValue}
									onChange={handleHangSliderChange}
								/>
							</HoverModalButton>
						</div>
					</HorizontalScroll>
					<StyledReturnButton
						onClick={() => applyBounceHandler(value)}
						onChange={onChange}
					/>
				</div>
			</div>
		</>
	);
}